import React, { FunctionComponent, useEffect, useState, useCallback } from 'react';
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import InputGroup from 'react-bootstrap/InputGroup';
import Nav from 'react-bootstrap/Nav';
import Accordion from 'react-bootstrap/Accordion';

import { Article, RootStore } from '../app/types';
import { setArticleContent, setArticleName, setArticlePublished } from '../api';
import { MediaGallery } from './MediaGallery';
import { Media } from '../app/types';

type ViewMode = 'edit' | 'preview' | 'split';

export const ArticleEditor: FunctionComponent = () => {
    const dispatch = useDispatch();
    const { articleID } = useParams();
    const isWideScreen = useMediaQuery({ minWidth: 768 });
    const [viewMode, setViewMode] = useState<ViewMode>(isWideScreen ? 'split' : 'edit');
    const [name, setName] = useState("");
    const [nameEdited, setNameEdited] = useState(false);
    const [content, setContent] = useState("");
    const [splitPosition, setSplitPosition] = useState(50); // percentage for left pane
    const [isDragging, setIsDragging] = useState(false);

    // Select article directly using find() - returns the same reference if unchanged
    const articleIdNum = parseInt(articleID);
    const article = useSelector((state: RootStore) =>
        state.articles.find((a: Article) => a.ID === articleIdNum) ?? null
    );

    const hasUnsavedContent = article && article.content !== content;
    const hasUnsavedName = article && article.name !== name;
    const hasUnsavedChanges = hasUnsavedContent || hasUnsavedName;

    useEffect(() => {
        setContent(""); // clear textarea if the articleID changes
    }, [articleID]);

    useEffect(() => {
        if (!content.length)
            setContent(article ? article.content : "");
    }, [content.length, article]);

    useEffect(() => {
        if (!nameEdited)
            setName(article ? article.name : "");
    }, [nameEdited, article]);

    const onSaveContent = useCallback(() => {
        setArticleContent(dispatch, articleID, content);
    }, [dispatch, articleID, content]);

    const onSaveName = useCallback(() => {
        setArticleName(dispatch, articleID, name);
    }, [dispatch, articleID, name]);

    const onSaveAll = useCallback(() => {
        if (hasUnsavedContent) onSaveContent();
        if (hasUnsavedName) onSaveName();
    }, [hasUnsavedContent, hasUnsavedName, onSaveContent, onSaveName]);

    // Handle split pane resizing
    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            const container = document.getElementById('split-container');
            if (!container) return;
            const rect = container.getBoundingClientRect();
            const newPosition = ((e.clientX - rect.left) / rect.width) * 100;
            setSplitPosition(Math.min(Math.max(newPosition, 20), 80)); // clamp between 20-80%
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    // Keyboard shortcut for save (Ctrl/Cmd + S)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                if (hasUnsavedContent) onSaveContent();
                if (hasUnsavedName) onSaveName();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [hasUnsavedContent, hasUnsavedName, onSaveContent, onSaveName]);

    function onTogglePublished() {
        setArticlePublished(dispatch, articleID, !article.published);
    }

    function onInjectHeader(media: Media) {
        const prot = window.location.protocol;
        const host = window.location.host;
        const url = prot+'//'+host+'/api/v1/media/'+media.ID+'/image';
        const md = '[' + media.shortKey + ']: '+ url + '\n\n![' + media.shortKey + ']\n\n';
        setContent(md + content);
    }

    const renderEditor = () => (
        <Form.Control
            as="textarea"
            value={content}
            onChange={e => setContent(e.target.value)}
            className="font-monospace border-0"
            style={{
                height: '100%',
                minHeight: '60vh',
                resize: 'none',
                outline: 'none',
                boxShadow: 'none'
            }}
            placeholder="Write your content in Markdown..."
        />
    );

    const renderPreview = () => (
        <div
            className="p-3 overflow-auto"
            style={{
                height: '100%',
                minHeight: '60vh',
                lineHeight: '1.7'
            }}
        >
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                {content || '*No content yet*'}
            </ReactMarkdown>
        </div>
    );

    if (!article) return <div />;

    return (
        <div className="d-flex flex-column vh-100 w-100">
            {/* Sticky Header */}
            <div
                className="border-bottom px-3 py-2"
                style={{ position: 'sticky', top: 0, zIndex: 100, backgroundColor: '#f8f9fa' }}
            >
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    {/* Title Input */}
                    <InputGroup style={{ flex: '1 1 300px', maxWidth: '600px' }}>
                        <Form.Control
                            id="title-input"
                            autoComplete="off"
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                setNameEdited(true);
                            }}
                            placeholder="Article title"
                            className="fw-semibold"
                            style={{
                                fontSize: '1.1rem',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                backgroundColor: '#fff',
                            }}
                        />
                    </InputGroup>

                    {/* Published Toggle */}
                    <Form.Check
                        type="switch"
                        id="published-switch"
                        label="Published"
                        checked={article.published}
                        onChange={onTogglePublished}
                        className="ms-auto"
                        style={{ fontSize: '0.9rem' }}
                    />

                    {/* Save Button */}
                    <Button
                        variant={hasUnsavedChanges ? "primary" : "outline-secondary"}
                        size="sm"
                        onClick={onSaveAll}
                        disabled={!hasUnsavedChanges}
                    >
                        {hasUnsavedChanges ? 'Save' : 'Saved'}
                    </Button>
                </div>
            </div>

            {/* Collapsible Media Gallery */}
            <Accordion className="border-bottom">
                <Accordion.Item eventKey="0" className="border-0">
                    <Accordion.Header className="py-0">
                        <small className="text-muted">Media Gallery</small>
                    </Accordion.Header>
                    <Accordion.Body className="py-2">
                        <MediaGallery
                            collectionID={article.collectionID}
                            articleID={article.ID}
                            onInjectHeader={onInjectHeader}
                        />
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            {/* View Mode Tabs */}
            <Nav
                variant="tabs"
                activeKey={viewMode}
                onSelect={(k) => setViewMode(k as ViewMode)}
                className="px-3 pt-2 bg-light border-bottom"
            >
                <Nav.Item>
                    <Nav.Link eventKey="edit">Edit</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="preview">Preview</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="split">Split</Nav.Link>
                </Nav.Item>
            </Nav>

            {/* Content Area */}
            <div className="flex-grow-1 overflow-hidden w-100">
                {viewMode === 'edit' && (
                    <div className="h-100 border-end">{renderEditor()}</div>
                )}

                {viewMode === 'preview' && (
                    <div className="h-100 bg-white">{renderPreview()}</div>
                )}

                {viewMode === 'split' && (
                    <div id="split-container" className="d-flex h-100" style={{ userSelect: isDragging ? 'none' : 'auto' }}>
                        <div className="overflow-auto" style={{ width: `${splitPosition}%` }}>
                            {renderEditor()}
                        </div>
                        <div
                            onMouseDown={() => setIsDragging(true)}
                            style={{
                                width: '6px',
                                cursor: 'col-resize',
                                backgroundColor: isDragging ? '#0d6efd' : '#dee2e6',
                                flexShrink: 0,
                                transition: isDragging ? 'none' : 'background-color 0.15s'
                            }}
                            onMouseEnter={(e) => { if (!isDragging) e.currentTarget.style.backgroundColor = '#adb5bd'; }}
                            onMouseLeave={(e) => { if (!isDragging) e.currentTarget.style.backgroundColor = '#dee2e6'; }}
                        />
                        <div className="bg-light overflow-auto" style={{ width: `calc(${100 - splitPosition}% - 6px)` }}>
                            {renderPreview()}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
